import React, { useEffect, useState } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Phone, MapPin, Plus, Minus, ShieldCheck } from 'lucide-react';
import { services, serviceDetails, dfwAreas, business } from '../mock';
import useReveal from '../hooks/useReveal';

export default function ServiceDetail() {
  const { id } = useParams();
  const { openSchedule } = useOutletContext();
  const service = services.find((s) => s.id === id);
  const detail = serviceDetails[id];
  const [openFaq, setOpenFaq] = useState(-1);
  useReveal();

  useEffect(() => { window.scrollTo({ top: 0 }); }, [id]);

  if (!service || !detail) {
    return (
      <div className="container-x" style={{ paddingTop: 160, paddingBottom: 120, textAlign: 'center' }}>
        <h1 className="text-3xl">Service not found</h1>
        <Link to="/" className="btn-sage mt-6 inline-flex">Back Home</Link>
      </div>
    );
  }

  const Icon = service.icon;
  const others = services.filter((s) => s.id !== id).slice(0, 3);

  return (
    <>
      {/* Hero */}
      <section style={{ paddingTop: 74, backgroundColor: 'var(--cream-2)' }}>
        <div className="container-x py-16 lg:py-20">
          <Link to="/#services" className="inline-flex items-center gap-2 text-sm font-medium mb-8" style={{ color: 'var(--muted)' }}>
            <ArrowLeft size={16} /> All Services
          </Link>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="flex items-center justify-center rounded-2xl mb-5" style={{ width: 60, height: 60, backgroundColor: 'var(--sage)' }}>
                <Icon size={28} style={{ color: 'var(--forest)' }} />
              </span>
              <p className="eyebrow mb-2">Care Service {service.no}</p>
              <h1 style={{ fontSize: 'clamp(2.2rem, 4.4vw, 3.4rem)' }}>{service.title}</h1>
              <p className="mt-5 text-lg" style={{ color: 'var(--muted)' }}>{detail.tagline}</p>
              <div className="flex flex-wrap gap-4 mt-8">
                <button className="btn-sage" onClick={openSchedule}>Schedule a Consultation <ArrowRight size={18} /></button>
                <a href={business.phoneHref} className="btn-outline"><Phone size={18} /> {business.phone}</a>
              </div>
            </div>
            <div className="rounded-card" style={{ aspectRatio: '4/3' }}>
              <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Overview + benefits */}
      <section className="section">
        <div className="container-x grid lg:grid-cols-[1.3fr_1fr] gap-14">
          <div className="reveal">
            <p className="eyebrow mb-2">Overview</p>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.6rem)' }}>Care built around your loved one</h2>
            <p className="mt-5" style={{ color: 'var(--muted)' }}>{detail.overview}</p>
            <div className="mt-8 p-6 rounded-2xl flex items-start gap-4" style={{ backgroundColor: 'var(--sage-soft)' }}>
              <ShieldCheck size={24} style={{ color: 'var(--forest)' }} className="mt-1 shrink-0" />
              <p className="text-[15px]" style={{ color: 'var(--ink)' }}>
                This is <strong>non-medical</strong> in-home care and personal assistance — not skilled
                nursing or medical treatment. We’re always clear about our scope of care.
              </p>
            </div>
          </div>
          <div className="reveal">
            <div className="bg-white rounded-3xl p-8" style={{ border: '1px solid var(--line)' }}>
              <h3 className="text-xl mb-5">What’s included</h3>
              <ul className="flex flex-col gap-4">
                {service.included.map((it) => (
                  <li key={it} className="flex items-start gap-3">
                    <span className="flex items-center justify-center rounded-full mt-0.5 shrink-0" style={{ width: 22, height: 22, backgroundColor: 'var(--sage)' }}>
                      <Check size={13} style={{ color: 'var(--forest)' }} strokeWidth={3} />
                    </span>
                    <span style={{ color: 'var(--ink)' }}>{it}</span>
                  </li>
                ))}
              </ul>
              <h3 className="text-xl mb-5 mt-8">How your family benefits</h3>
              <ul className="flex flex-col gap-4">
                {detail.benefits.map((it) => (
                  <li key={it} className="flex items-start gap-3">
                    <span className="flex items-center justify-center rounded-full mt-0.5 shrink-0" style={{ width: 22, height: 22, backgroundColor: 'var(--pill-yellow)' }}>
                      <Check size={13} style={{ color: 'var(--forest)' }} strokeWidth={3} />
                    </span>
                    <span style={{ color: 'var(--ink)' }}>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* DFW proof */}
      <section className="section" style={{ backgroundColor: 'var(--forest)' }}>
        <div className="container-x text-center">
          <p className="eyebrow mb-2" style={{ color: '#B8D89E' }}>Local &amp; Trusted</p>
          <h2 className="text-white" style={{ color: '#fff', fontSize: 'clamp(1.8rem, 3.4vw, 2.6rem)' }}>
            Proudly serving the Dallas–Fort Worth metroplex
          </h2>
          <p className="mt-4 text-white/75 max-w-[640px] mx-auto">
            Families across DFW have trusted Mom’s Best Friend Senior Care since {business.since}.
            We bring {service.title.toLowerCase()} right to your neighborhood.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-10 max-w-[860px] mx-auto">
            {dfwAreas.map((a) => (
              <span key={a} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm text-white/90" style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)' }}>
                <MapPin size={14} style={{ color: 'var(--sage)' }} /> {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section">
        <div className="container-x grid lg:grid-cols-[0.8fr_1.2fr] gap-12">
          <div className="reveal">
            <p className="eyebrow mb-2">Questions</p>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.6rem)' }}>{service.title} FAQs</h2>
            <p className="mt-5" style={{ color: 'var(--muted)' }}>Still have questions? We’re always happy to talk it through — no pressure.</p>
            <button className="btn-sage mt-7" onClick={openSchedule}>Talk With Us <ArrowRight size={18} /></button>
          </div>
          <div className="reveal flex flex-col gap-3">
            {detail.faqs.map((f, idx) => {
              const on = idx === openFaq;
              return (
                <div key={f.q} className="rounded-2xl bg-white overflow-hidden" style={{ border: '1px solid var(--line)' }}>
                  <button onClick={() => setOpenFaq(on ? -1 : idx)} className="w-full flex items-center justify-between gap-4 text-left px-6 py-5">
                    <span className="font-semibold text-[17px]" style={{ color: 'var(--forest)' }}>{f.q}</span>
                    <span className="flex items-center justify-center rounded-full shrink-0" style={{ width: 32, height: 32, backgroundColor: on ? 'var(--forest)' : 'var(--sage-soft)' }}>
                      {on ? <Minus size={17} color="#fff" /> : <Plus size={17} style={{ color: 'var(--forest)' }} />}
                    </span>
                  </button>
                  <div style={{ maxHeight: on ? 240 : 0, transition: 'max-height 0.4s ease', overflow: 'hidden' }}>
                    <p className="px-6 pb-6" style={{ color: 'var(--muted)' }}>{f.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="section" style={{ backgroundColor: 'var(--cream-2)' }}>
        <div className="container-x">
          <h2 className="text-center" style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.4rem)' }}>Explore other care options</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {others.map((s) => {
              const OIcon = s.icon;
              return (
                <Link key={s.id} to={`/services/${s.id}`} className="bg-white rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1" style={{ border: '1px solid var(--line)' }}>
                  <span className="flex items-center justify-center rounded-2xl mb-5" style={{ width: 52, height: 52, backgroundColor: 'var(--sage-soft)' }}>
                    <OIcon size={26} style={{ color: 'var(--forest)' }} />
                  </span>
                  <h3 className="text-lg">{s.title}</h3>
                  <p className="mt-2 text-[15px]" style={{ color: 'var(--muted)' }}>{s.short}</p>
                  <span className="inline-flex items-center gap-2 mt-4 text-sm font-semibold" style={{ color: 'var(--forest)' }}>Learn more <ArrowRight size={15} /></span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
